import axios, { type AxiosInstance } from 'axios';
import * as Sentry from '@sentry/react';

import { buildLoginRedirect, isOnAuthRoute } from '@/lib/auth/loginRedirect';
import { resolveLogoutDestination } from '@/lib/auth/resolveLogoutDestination';
import { normalizeError } from '@/lib/errors';

export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8800/api/v1';
const REALTIME_BASE_URL: string =
  (import.meta.env.VITE_REALTIME_BASE_URL as string | undefined) ?? 'http://localhost:8801/api/v1';

const AUTH_ENDPOINT_PATTERNS = [
  /^\/auth\/login$/,
  /^\/auth\/mfa\//,
  /^\/auth\/reset-password\//,
  /^\/auth\/invite\/accept$/,
  /^\/auth\/invite\/check$/,
  /^\/auth\/refresh$/,
  /^\/clients\/register$/,
];

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api\/v\d+/, '');
  return AUTH_ENDPOINT_PATTERNS.some((pattern) => pattern.test(path));
}

function createAxiosInstance(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
    // Repeat-encode array params (`{ type: ['a','b'] }` → `?type=a&type=b`)
    // to match the BE's `buildListQuerySchema` contract.
    paramsSerializer: { indexes: null },
  });

  // Request: attach Bearer token lazily (avoids circular dep with authStore).
  instance.interceptors.request.use(async (config) => {
    if (config.headers.Authorization) return config;
    const { useAuthStore } = await import('@/stores/authStore');
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response: normalize errors, handle 401 (with silent refresh) / 403 / 429 / 5xx.
  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const original = error.config as
          | (NonNullable<typeof error.config> & { _retry?: boolean })
          | undefined;

        if (status === 401 && original && !isAuthEndpoint(original.url) && !original._retry) {
          original._retry = true;
          try {
            const { coalescedRefresh } = await import('@/lib/auth/tokenRefresh');
            const newToken = await coalescedRefresh();
            original.headers.Authorization = `Bearer ${newToken}`;
            return await instance(original);
          } catch {
            // coalescedRefresh already wiped storage and triggered the redirect.
            return Promise.reject(normalizeError(error));
          }
        }

        if (status === 401 && !isAuthEndpoint(error.config?.url)) {
          // Refresh-not-applicable 401 (already retried, missing refresh
          // token). Storage-only wipe + replace() — see authStore.ts:88-99
          // for first-paint-flash reasoning.
          const { useAuthStore, clearAuthStorage } = await import('@/stores/authStore');
          const { cancelProactiveRefresh } = await import('@/lib/auth/tokenRefresh');
          const { user } = useAuthStore.getState();
          const loginPath = resolveLogoutDestination(user);
          cancelProactiveRefresh();
          clearAuthStorage();
          if (!isOnAuthRoute()) {
            window.location.replace(buildLoginRedirect(loginPath));
          }
        } else if (status === 403) {
          const body = error.response?.data as
            | { code?: string; missingDays?: string[] }
            | undefined;
          if (body?.code === 'AVAILABILITY_OVERDUE') {
            const { useCadenceStore } = await import('@/features/availability/store/cadenceStore');
            useCadenceStore.getState().openModal({
              missingDays: body.missingDays ?? [],
              originalRoute: window.location.pathname + window.location.search,
            });
          } else {
            // RBAC drift — route guards should have caught this earlier.
            Sentry.captureMessage('API 403 (possible RBAC drift)', {
              level: 'warning',
              extra: { url: error.config?.url, method: error.config?.method },
            });
          }
        } else if (status === 429) {
          console.warn('Rate limit exceeded. Please wait a moment before retrying.');
        } else if (status && status >= 500) {
          Sentry.captureException(error, {
            tags: { kind: 'api_5xx' },
            extra: { url: error.config?.url, method: error.config?.method, status },
          });
        }
      }

      return Promise.reject(normalizeError(error));
    },
  );

  return instance;
}

export const apiClient = createAxiosInstance(API_BASE_URL);
export const realtimeClient = createAxiosInstance(REALTIME_BASE_URL);
